import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PublishModal from "./PublishModal/PublishModal";
import { PimDashboardState } from "context/PimDashboardContext";
import { attrGroupLinkedTables, productionStageOptions, productPagePath } from "components/PimDashboard/data";
import { styleUrlKey } from "../CreateStyle/data";
import {
	converAttrToDatagridCol,
	createdByColumns,
	datatypes,
	imageTableColumn,
	publishColumns,
	stageColumn,
	stageTableColumn,
	statusOptions,
	styleFabricUpdationColumns,
	styleMrpUpdationColumns,
	styleImageColumn,
} from "data/globalData";
import {
	ChevronLeftRounded,
	ChevronRightRounded,
	CloseRounded,
	FileDownloadRounded,
	FilterAltRounded,
	InfoRounded,
	Publish,
	SearchRounded,
	ViewColumnRounded,
} from "@mui/icons-material";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { lsStylesFilterValStr, hasRemarkFilter, stylesDashboardExportOptions } from "./data";
import RemarkModal from "./RemarkModal/RemarkModal";
import ViewEditRemarkModal from "./ViewEditRemarkModal/ViewEditRemarkModal";
import DataExportModal from "./DataExportModal/DataExportModal";

const PAGE_SIZE = 20;

const palette = {
	ink: "#111110",
	ink2: "#3a3a38",
	ink3: "#6b6b68",
	ink4: "#9b9b97",
	surface: "#ffffff",
	surface2: "#f7f7f5",
	surface3: "#f0efec",
	border: "rgba(0,0,0,0.10)",
	border2: "rgba(0,0,0,0.14)",
	green: "#2a7a4f",
	greenBg: "#eaf4ee",
	red: "#b83232",
	redBg: "#fdeaea",
};

/** Pending = every non-PUBLISHED status key from statusOptions (product parity). */
const DEFAULT_COLUMN_ORDER = [
	"style_id",
	"style_name",
	"season",
	"order_type",
	"brand",
	"sub_brand",
	"division",
	"gender",
	"category",
	"sub_category",
];

const HEADER_NAME_OVERRIDES = {
	style_id: "Style ID",
	style_name: "Name",
	season: "Season",
	order_type: "Order",
	brand: "Brand",
	sub_brand: "Sub brand",
	division: "Division",
	gender: "Gender",
	category: "Category",
	sub_category: "Sub category",
	color_count: "Colors",
	created_by: "Created by",
	last_updated_by: "Last updated by",
	stage: "Stage",
	url: "Image",
	status: "Status",
};

/** Fixed widths so columns don't flex-shrink to the viewport (enables horizontal scroll). */
const COLUMN_WIDTHS = {
	status: 150,
	url: 88,
	style_id: 130,
	style_name: 200,
	season: 100,
	order_type: 110,
	brand: 130,
	sub_brand: 120,
	division: 110,
	gender: 90,
	category: 120,
	sub_category: 130,
	color_count: 80,
	created_by: 160,
	last_updated_by: 160,
	stage: 120,
};

const HIDDEN_BY_DEFAULT_FIELDS = new Set(["stage", "last_updated_by"]);

const colStyle = {
	headerAlign: "left",
	align: "left",
};

const formatCount = (n) => Number(n || 0).toLocaleString("en-US");

const formatFilterChipValue = (value) => {
	if (Array.isArray(value)) return value.join(", ");
	if (value == null) return "";
	return String(value);
};

const hasActiveFilterValue = (value) => {
	if (value === undefined || value === null || value === "") return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
};

/** Kept exported — imported by FabricAndTrims.js (even if call sites are commented). */
export function setStylesCustomFun(prev, newRows) {
	const map = {};
	return [...prev, ...newRows]?.reduce((acc, row) => {
		if (map[row.style_id]) return acc;
		acc.push(row);
		map[row.style_id] = true;
		return acc;
	}, []);
}

const StatusPill = ({ status }) => {
	const option = statusOptions[status];
	const label = option?.name || status || "Pending";
	const bg = option?.color || palette.redBg;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1,
				py: 0.25,
				borderRadius: 999,
				fontSize: 11,
				fontWeight: 600,
				lineHeight: 1.4,
				bgcolor: bg,
				color: "#fff",
				whiteSpace: "nowrap",
			}}>
			{label}
		</Box>
	);
};

const ToolbarButton = ({ icon, label, onClick }) => (
	<Button
		variant="outlined"
		size="small"
		onClick={onClick}
		startIcon={icon}
		sx={{
			textTransform: "none",
			fontSize: 12,
			fontWeight: 500,
			color: palette.ink2,
			borderColor: palette.border2,
			bgcolor: palette.surface2,
			borderRadius: 1.25,
			px: 1.25,
			py: 0.5,
			minHeight: 32,
			"&:hover": { borderColor: palette.ink3, bgcolor: palette.surface3 },
		}}>
		{label}
	</Button>
);

/**
 * @param onFilterChange - notified with { filter, search } whenever the active filter or debounced
 *   search changes, so siblings (StylesAnalytics) can scope to the same rows.
 * @param onFilterUiChange - notified with { open, count } so the shell can put More Filters at page top.
 */
const StylesDashboard = ({ onFilterChange, onFilterUiChange }) => {
	const selectedRowsByIdRef = useRef({});
	const requestIdRef = useRef(0);
	const { allAttributes, allAttributesObj } = PimDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [styles, setStyles] = useState([]);
	const [displayImg, setDisplayImg] = useState(true);
	const [selectedStyleToPublish, setSelectedStyleToPublish] = useState();
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [publishModal, setPublishModal] = useState(false);
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [reviewStyles, setReviewStyles] = useState([]);
	const [remarkModal, setRemarkModal] = useState(false);
	const [viewEditRemarkModal, setViewEditRemarkModal] = useState(false);
	const [selectedStyleForRemark, setSelectedStyleForRemark] = useState(null);
	const [exportModal, setExportModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [page, setPage] = useState(1);
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({
		stage: false,
		last_updated_by: false,
		[styleImageColumn.key]: true,
	});

	const defColumns = [
		{
			field: "color_count",
			headerName: "Colors",
			width: 80,
			...colStyle,
		},
		...createdByColumns.map((row) => converAttrToDatagridCol(row)),
	];

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem("styles_table_attr")) {
			return JSON.parse(localStorage.getItem("styles_table_attr"))
				?.map((row) => {
					return { ...allAttributesObj?.[row] };
				})
				.map((row) => converAttrToDatagridCol(row));
		}
		return allAttributes
			?.filter((row) => row?.linked_table === attrGroupLinkedTables.style.key)
			?.map((row) => converAttrToDatagridCol(row));
	});

	const [filterColumns, setFilterColumns] = useState(() => {
		let final = [];
		if (localStorage.getItem(lsStylesFilterValStr)) {
			const localStorageObj =
				typeof JSON.parse(localStorage.getItem(lsStylesFilterValStr)) === "object"
					? JSON.parse(localStorage.getItem(lsStylesFilterValStr))
					: {};
			[
				...defColumns,
				...allAttributes,
				...createdByColumns,
				hasRemarkFilter,
				stageColumn,
				...Object.keys(publishColumns).map((row) => publishColumns[row]),
				...styleFabricUpdationColumns,
				...styleMrpUpdationColumns,
			]?.forEach((row) => {
				if (localStorageObj?.[row?.key]) final.push(row);
			});
			return final;
		}
		return final;
	});

	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(lsStylesFilterValStr)) {
			return JSON.parse(localStorage.getItem(lsStylesFilterValStr));
		}
		return {};
	});

	const navigate = useNavigate();

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
		return () => clearTimeout(t);
	}, [searchInput]);

	const filterAttributeCatalog = useMemo(() => {
		const byKey = {};
		[
			...(allAttributes || []),
			...createdByColumns,
			...Object.keys(publishColumns).map((row) => publishColumns[row]),
			...styleFabricUpdationColumns,
			...styleMrpUpdationColumns,
			stageColumn,
		].forEach((attr) => {
			if (attr?.key) byKey[attr.key] = attr;
		});
		return byKey;
	}, [allAttributes]);

	/**
	 * Server-side pagination: send paginate:true so designer getstyles keeps LIMIT/OFFSET
	 * even when filters/search are present (legacy callers without the flag stay unlimited).
	 */
	const buildApiFilter = useCallback(() => {
		const filter = { ...(filterColumnVal || {}) };

		// Only override style_id / style_name when the search box is active.
		// Empty search must leave Filter-modal style_id/style_name conditions intact.
		if (debouncedSearch) {
			if (/-/.test(debouncedSearch)) {
				filter.style_id = debouncedSearch;
				delete filter.style_name;
			} else {
				filter.style_name = debouncedSearch;
				delete filter.style_id;
			}
		}

		return filter;
	}, [filterColumnVal, debouncedSearch]);

	// Publish the raw filter (not buildApiFilter's output — that folds search into style_id/style_name,
	// which the analytics endpoint takes as a separate `search` term).
	useEffect(() => {
		onFilterChange?.({ filter: filterColumnVal || {}, search: debouncedSearch });
	}, [filterColumnVal, debouncedSearch, onFilterChange]);

	const rememberRows = useCallback((rows = []) => {
		rows.forEach((row) => {
			if (row?.style_id) selectedRowsByIdRef.current[row.style_id] = row;
		});
	}, []);

	const patchStyleEverywhere = useCallback((styleId, patch) => {
		if (selectedRowsByIdRef.current[styleId]) {
			selectedRowsByIdRef.current[styleId] = {
				...selectedRowsByIdRef.current[styleId],
				...patch,
			};
		}
		setStyles((prev) => prev.map((row) => (row.style_id === styleId ? { ...row, ...patch } : row)));
		setReviewStyles((prev) => prev.map((row) => (row.style_id === styleId ? { ...row, ...patch } : row)));
	}, []);

	const handleRemarkClick = (e, styleData) => {
		e.stopPropagation();
		setSelectedStyleForRemark(styleData);
		setViewEditRemarkModal(true);
	};

	const handlePublishClick = (e, styleData) => {
		e.stopPropagation();
		setPublishModal(true);
		setSelectedStyleToPublish(styleData);
	};

	const canPublishRow = useCallback(
		(row) => {
			if (!allowAccess(custom.STYLE_PUBLISHER.key, user, departments.DESIGNER.key)) return false;
			return row?.status === statusOptions.DESIGNER_PENDING.key;
		},
		[user],
	);

	const statusColumn = useMemo(
		() => ({
			field: "status",
			headerName: "Status",
			width: COLUMN_WIDTHS.status,
			minWidth: COLUMN_WIDTHS.status,
			flex: 0,
			sortable: true,
			...colStyle,
			renderCell: ({ row }) => {
				const showPublish = canPublishRow(row);
				const hasRemark = !!row?.remark_id;
				return (
					<Box display="flex" alignItems="center" height="100%" columnGap={0.5} onClick={(e) => e.stopPropagation()}>
						<StatusPill status={row?.status} />
						{hasRemark && (
							<IconButton size="small" color="warning" sx={{ p: 0.25 }} onClick={(e) => handleRemarkClick(e, row)}>
								<InfoRounded sx={{ fontSize: 16 }} />
							</IconButton>
						)}
						{showPublish && (
							<Button
								size="small"
								variant="text"
								startIcon={<Publish sx={{ fontSize: 14 }} />}
								onClick={(e) => handlePublishClick(e, row)}
								sx={{ textTransform: "none", fontSize: 11, minWidth: 0, px: 0.5, color: palette.ink2 }}>
								Publish
							</Button>
						)}
					</Box>
				);
			},
		}),
		[canPublishRow],
	);

	const decorateColumn = (col) => {
		if (!col?.field) return col;
		const headerName = HEADER_NAME_OVERRIDES[col.field] || col.headerName;
		const attr = filterAttributeCatalog[col.field] || allAttributesObj?.[col.field];
		const isNumber = attr?.data_type === datatypes.NUMBER?.key || col.type === "number" || col.field === "color_count";
		const width = COLUMN_WIDTHS[col.field] || col.width || col.minWidth || 120;
		// Drop flex so MUI doesn't shrink columns into the viewport (blocks horizontal scroll).
		const { flex: _flex, ...colWithoutFlex } = col;
		const base = {
			...colWithoutFlex,
			headerName,
			headerAlign: isNumber ? "right" : "left",
			align: isNumber ? "right" : "left",
			sortable: col.sortable !== false,
			width,
			minWidth: width,
			flex: 0,
		};
		if (col.field === "created_by" || col.field === "last_updated_by") {
			return {
				...base,
				renderCell: ({ value }) => (
					<Tooltip title={value || ""}>
						<Typography
							component="span"
							sx={{
								fontSize: 12,
								color: palette.ink2,
								maxWidth: width - 16,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								display: "block",
							}}>
							{value || "—"}
						</Typography>
					</Tooltip>
				),
			};
		}
		return base;
	};

	const styleTablColumns = useMemo(() => {
		const byField = {};
		(attrColumns || []).forEach((col) => {
			if (col?.field) byField[col.field] = col;
		});
		defColumns.forEach((col) => {
			if (col?.field) byField[col.field] = col;
		});

		const orderedAttr = [];
		DEFAULT_COLUMN_ORDER.forEach((key) => {
			if (byField[key]) {
				orderedAttr.push(decorateColumn(byField[key]));
				delete byField[key];
			}
		});

		const colorCol = decorateColumn(byField.color_count || defColumns.find((c) => c.field === "color_count"));
		const createdCol = decorateColumn(
			byField.created_by || defColumns.find((c) => c.field === "created_by") || converAttrToDatagridCol(createdByColumns[0]),
		);
		const lastUpdated = decorateColumn(
			byField.last_updated_by ||
				defColumns.find((c) => c.field === "last_updated_by") ||
				converAttrToDatagridCol(createdByColumns[1]),
		);
		delete byField.color_count;
		delete byField.created_by;
		delete byField.last_updated_by;
		delete byField.stage;
		delete byField.status;
		delete byField.url;

		const extras = Object.values(byField).map(decorateColumn);
		const stageCol = decorateColumn(stageTableColumn(productionStageOptions));
		const imageCol = decorateColumn(imageTableColumn(styleImageColumn));

		return [statusColumn, imageCol, ...orderedAttr, colorCol, createdCol, stageCol, lastUpdated, ...extras].filter(Boolean);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attrColumns, statusColumn, filterAttributeCatalog, allAttributesObj]);

	const activeFilterCount = useMemo(
		() => Object.keys(filterColumnVal || {}).filter((key) => hasActiveFilterValue(filterColumnVal[key])).length,
		[filterColumnVal],
	);

	const removeActiveFilter = (key) => {
		const nextVal = { ...(filterColumnVal || {}) };
		delete nextVal[key];
		setFilterColumnVal(nextVal);
		setFilterColumns((prev) => (prev || []).filter((col) => col?.key !== key));
		if (Object.keys(nextVal).length) {
			localStorage.setItem(lsStylesFilterValStr, JSON.stringify(nextVal));
		} else {
			localStorage.removeItem(lsStylesFilterValStr);
		}
	};

	// Default visibility: Stage / last_updated_by hidden; managed extras visible; image follows toggle.
	useEffect(() => {
		setColumnVisibilityModel((prev) => {
			const next = { ...prev };
			(styleTablColumns || []).forEach((col) => {
				if (!col?.field) return;
				if (col.field === styleImageColumn.key) {
					next[col.field] = displayImg;
					return;
				}
				if (HIDDEN_BY_DEFAULT_FIELDS.has(col.field)) {
					if (!(col.field in prev)) next[col.field] = false;
					return;
				}
				if (!(col.field in prev)) next[col.field] = true;
			});
			next[styleImageColumn.key] = displayImg;
			return next;
		});
	}, [styleTablColumns, displayImg]);

	const totalPages = Math.max(1, Math.ceil(Number(tableStatusCount.total || 0) / PAGE_SIZE) || 1);
	const showingFrom = tableStatusCount.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const showingTo = Math.min(page * PAGE_SIZE, Number(tableStatusCount.total || 0));

	const fetchPage = useCallback(
		async (nextPage = 1) => {
			const filter = buildApiFilter();
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});
			const requestId = ++requestIdRef.current;
			const offset = (nextPage - 1) * PAGE_SIZE;
			setLoading(true);
			setLoadingOpen(true);
			try {
				const { data } = await axios.post(`/api/designer/styles/${offset}`, {
					filter,
					extraColumns,
					limit: PAGE_SIZE,
					paginate: true,
				});
				if (requestId !== requestIdRef.current) return;
				const rows = data?.result?.styles || [];
				rememberRows(rows);
				const count = data?.result?.count?.[0] || { total: 0, published: 0, pending: 0 };
				setTableStatusCount(count);
				setStyles(rows);
				setPage(nextPage);
			} catch (error) {
				if (requestId !== requestIdRef.current) return;
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
			} finally {
				if (requestId === requestIdRef.current) {
					setLoading(false);
					setLoadingOpen(false);
				}
			}
		},
		[attrColumns, buildApiFilter, rememberRows, setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar],
	);

	const reloadStyles = async () => {
		await fetchPage(page || 1);
	};

	useEffect(() => {
		fetchPage(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterColumns, attrColumns, debouncedSearch, filterColumnVal]);

	const handleCloseFilterModal = () => setFilterModal(false);
	const handleCloseAddColumnModal = () => setAddColumnModal(false);
	const handleCloseExportModal = () => setExportModal(false);
	const handleFilterClick = useCallback(() => setFilterModal(true), []);
	const handleAddColumnClick = () => setAddColumnModal(true);
	const handleDownloadClick = () => setExportModal(true);

	useEffect(() => {
		onFilterUiChange?.({ open: handleFilterClick, count: activeFilterCount });
	}, [onFilterUiChange, handleFilterClick, activeFilterCount]);

	const handleStyleSelect = async (data) => {
		navigate(`/${productPagePath}?${styleUrlKey}=${data.style_id}`);
	};

	const onPublishFinalSubmit = (styleData) => {
		if (styleData?.style_id) patchStyleEverywhere(styleData.style_id, styleData);
		setPublishModal(false);
		setSelectedStyleToPublish();
	};

	const clearSearch = () => setSearchInput("");

	const goToPage = (p) => {
		const next = Math.min(Math.max(1, p), totalPages);
		if (next !== page) fetchPage(next);
	};

	const handleSelectionModelChange = (newSelectionModel) => {
		const nextIds = Array.isArray(newSelectionModel) ? newSelectionModel : [];
		rememberRows(styles);
		setRowSelectionModel(nextIds);
		setReviewStyles(nextIds.map((id) => selectedRowsByIdRef.current[id]).filter(Boolean));
	};

	const pageButtons = useMemo(() => {
		const windowSize = Math.min(5, totalPages);
		const start = Math.min(Math.max(1, page - Math.floor(windowSize / 2)), Math.max(1, totalPages - windowSize + 1));
		return Array.from({ length: windowSize }, (_, i) => start + i);
	}, [page, totalPages]);

	return (
		<Stack width="100%" pt={1} sx={{ bgcolor: "#fff" }}>
			<Box
				sx={{
					border: `1px solid ${palette.border}`,
					borderRadius: 2,
					bgcolor: palette.surface,
					px: { xs: 1.5, md: 2.5 },
					py: 2,
					overflow: "hidden",
				}}>
				{/* Search + More Filters */}
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
					<TextField
						size="small"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						placeholder="Search style ID or name..."
						inputProps={{ "aria-label": "Search style ID or name" }}
						sx={{
							minWidth: { xs: "100%", sm: 320 },
							flex: { sm: "0 1 420px" },
							"& .MuiOutlinedInput-root": {
								borderRadius: 1.5,
								fontSize: 13,
								bgcolor: "#fff",
								"& fieldset": { borderColor: palette.border2 },
							},
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchRounded sx={{ fontSize: 18, color: palette.ink4 }} />
								</InputAdornment>
							),
						}}
					/>
					<Button
						size="small"
						variant="outlined"
						startIcon={<CloseRounded sx={{ fontSize: 14 }} />}
						onClick={clearSearch}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: palette.ink2,
							borderColor: palette.border2,
							borderRadius: 1,
							minHeight: 32,
						}}>
						Clear
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
						onClick={handleFilterClick}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: palette.ink2,
							borderColor: activeFilterCount ? palette.ink2 : palette.border2,
							bgcolor: activeFilterCount ? palette.surface3 : "#fff",
							borderRadius: 1,
							minHeight: 32,
							fontWeight: activeFilterCount ? 600 : 500,
						}}>
						More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
					</Button>
				</Box>

				{/* Compact active filter summary (not permanent value chips for every option) */}
				{filterColumns?.length > 0 && (
					<Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={1.5}>
						{filterColumns.map((row) => (
							<Chip
								key={row.key}
								size="small"
								variant="outlined"
								onDelete={() => removeActiveFilter(row.key)}
								label={`${row.name}: ${formatFilterChipValue(filterColumnVal[row?.key])}`}
								sx={{
									height: 26,
									fontSize: 11,
									borderRadius: 1,
									borderColor: palette.border2,
									bgcolor: palette.surface2,
									color: palette.ink2,
									maxWidth: 280,
									"& .MuiChip-label": {
										overflow: "hidden",
										textOverflow: "ellipsis",
									},
								}}
							/>
						))}
					</Box>
				)}

				{/* Table toolbar */}
				<Box
					display="flex"
					alignItems="center"
					justifyContent="flex-end"
					gap={1.5}
					flexWrap="wrap"
					mb={1.25}>
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mr: "auto" }}>
						{reviewStyles.length > 0 && (
							<Button
								variant="contained"
								size="small"
								color="warning"
								onClick={() => setRemarkModal(true)}
								sx={{ textTransform: "none", fontSize: 12 }}>
								Add Remark ({reviewStyles.length})
							</Button>
						)}
					</Box>
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
						<ToolbarButton
							icon={<ViewColumnRounded sx={{ fontSize: 16 }} />}
							label="Columns"
							onClick={handleAddColumnClick}
						/>
						<ToolbarButton
							icon={<FileDownloadRounded sx={{ fontSize: 16 }} />}
							label="Export"
							onClick={handleDownloadClick}
						/>
						<FormControlLabel
							control={
								<Switch
									size="small"
									checked={displayImg}
									onChange={(e) => setDisplayImg(e.target.checked)}
									inputProps={{ "aria-label": "Toggle style images column" }}
									sx={{
										"& .MuiSwitch-switchBase.Mui-checked": { color: palette.ink },
										"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: palette.ink },
									}}
								/>
							}
							label={<Typography sx={{ fontSize: 12, color: palette.ink2 }}>Images</Typography>}
							sx={{ ml: 0.5, mr: 0 }}
						/>
					</Box>
				</Box>

				{/* Grid — auto height (no vertical scroll); horizontal scroll when columns overflow */}
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<DataGrid
						rows={styles || []}
						columns={styleTablColumns ?? []}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={(model) =>
							setColumnVisibilityModel({
								...model,
								[styleImageColumn.key]: displayImg,
							})
						}
						autoHeight
						sx={{
							border: "none",
							width: "100%",
							fontSize: 12,
							color: palette.ink2,
							"& .MuiDataGrid-main": {
								overflowX: "auto",
								overflowY: "hidden",
							},
							"& .MuiDataGrid-columnHeaders": {
								bgcolor: palette.surface3,
								borderBottom: `1px solid ${palette.border2}`,
								minHeight: "40px !important",
								maxHeight: "40px !important",
							},
							"& .MuiDataGrid-columnHeader": {
								bgcolor: palette.surface3,
								color: palette.ink2,
								fontSize: 12,
								fontWeight: 600,
							},
							"& .MuiDataGrid-columnHeaderTitle": {
								fontWeight: 600,
								color: palette.ink2,
							},
							"& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle": {
								color: palette.ink,
							},
							"& .MuiDataGrid-sortIcon": {
								opacity: 1,
								color: palette.ink3,
							},
							"& .MuiDataGrid-columnSeparator": {
								color: palette.border,
							},
							"& .MuiDataGrid-columnHeaderCheckbox": {
								bgcolor: palette.surface3,
								borderRight: `1px solid ${palette.border}`,
							},
							"& .MuiDataGrid-cellCheckbox": {
								borderRight: `1px solid ${palette.border}`,
							},
							"& .MuiDataGrid-row": {
								cursor: "pointer",
								borderBottom: `1px solid ${palette.border}`,
								"&:nth-of-type(even)": { bgcolor: palette.surface2 },
								"&:hover": { bgcolor: palette.surface3 },
							},
							"& .MuiDataGrid-cell": {
								borderBottom: "none",
								fontSize: 12,
								py: 0.5,
							},
							"& .MuiDataGrid-footerContainer": { display: "none" },
							"& .MuiDataGrid-virtualScroller": {
								overflowX: "auto !important",
								overflowY: "hidden !important",
							},
							"& .MuiDataGrid-virtualScrollerContent": {
								minWidth: "max-content",
							},
							"& .MuiDataGrid-overlayWrapper": {
								minHeight: 120,
							},
						}}
						columnHeaderHeight={40}
						rowHeight={displayImg ? 56 : 40}
						getRowId={(row) => row?.style_id}
						onRowClick={(params) => handleStyleSelect(params.row)}
						disableRowSelectionOnClick
						hideFooter
						pagination={false}
						disableColumnSelector
						disableAutosize
						sortingOrder={["asc", "desc"]}
						checkboxSelection={user && allowAccess(custom.STYLE_REVIEWER.key, user, departments.MDM.key)}
						isRowSelectable={(params) =>
							!params.row.remark_id && params.row.status === statusOptions.PUBLISHED.key
						}
						rowSelectionModel={rowSelectionModel}
						onRowSelectionModelChange={handleSelectionModelChange}
						keepNonExistentRowsSelected
						slots={{
							noRowsOverlay: () => (
								<Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight={120}>
									{loading ? (
										<CircularProgress size={22} />
									) : (
										<Typography sx={{ fontSize: 13, color: palette.ink3 }}>No styles match your filters</Typography>
									)}
								</Box>
							),
						}}
					/>
				</Box>

				{/* Pagination */}
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap={1.5}
					flexWrap="wrap"
					pt={1.5}
					mt={0.5}
					borderTop={`1px solid ${palette.border}`}>
					<Typography sx={{ fontSize: 12, color: palette.ink3 }}>
						{tableStatusCount.total
							? `Showing ${showingFrom}–${showingTo} of ${formatCount(tableStatusCount.total)}`
							: "No results"}
					</Typography>
					{tableStatusCount.total > 0 && (
						<Box display="flex" alignItems="center" gap={0.5}>
							<IconButton
								size="small"
								aria-label="Previous page"
								disabled={page <= 1 || loading}
								onClick={() => goToPage(page - 1)}
								sx={{
									width: 28,
									height: 28,
									borderRadius: 1,
									border: `1px solid ${palette.border2}`,
								}}>
								<ChevronLeftRounded sx={{ fontSize: 16 }} />
							</IconButton>
							{pageButtons[0] > 1 && (
								<Typography sx={{ fontSize: 11, color: palette.ink4, px: 0.5 }}>…</Typography>
							)}
							{pageButtons.map((p) => (
								<Button
									key={p}
									size="small"
									onClick={() => goToPage(p)}
									disabled={loading}
									aria-current={p === page ? "page" : undefined}
									sx={{
										minWidth: 28,
										height: 28,
										p: 0,
										borderRadius: 1,
										fontSize: 12,
										fontWeight: 600,
										bgcolor: p === page ? palette.ink : "#fff",
										color: p === page ? "#fff" : palette.ink2,
										border: `1px solid ${p === page ? palette.ink : palette.border2}`,
										"&:hover": {
											bgcolor: p === page ? palette.ink : palette.surface2,
										},
									}}>
									{p}
								</Button>
							))}
							{pageButtons[pageButtons.length - 1] < totalPages && (
								<>
									<Typography sx={{ fontSize: 11, color: palette.ink4, px: 0.5 }}>…</Typography>
									<Button
										size="small"
										onClick={() => goToPage(totalPages)}
										disabled={loading}
										aria-current={page === totalPages ? "page" : undefined}
										sx={{
											minWidth: 28,
											height: 28,
											p: 0,
											borderRadius: 1,
											fontSize: 12,
											color: palette.ink2,
											border: `1px solid ${palette.border2}`,
										}}>
										{totalPages}
									</Button>
								</>
							)}
							<IconButton
								size="small"
								aria-label="Next page"
								disabled={page >= totalPages || loading}
								onClick={() => goToPage(page + 1)}
								sx={{
									width: 28,
									height: 28,
									borderRadius: 1,
									border: `1px solid ${palette.border2}`,
								}}>
								<ChevronRightRounded sx={{ fontSize: 16 }} />
							</IconButton>
						</Box>
					)}
				</Box>
			</Box>

			<PublishModal
				modal={publishModal}
				setModal={setPublishModal}
				styleData={selectedStyleToPublish}
				setStyleData={setSelectedStyleToPublish}
				submitFunction={onPublishFinalSubmit}
			/>
			<FilterModal
				modal={filterModal}
				handleCloseModal={handleCloseFilterModal}
				filterColumns={filterColumns}
				setFilterColumns={setFilterColumns}
				filterColumnVal={filterColumnVal}
				setFilterColumnVal={setFilterColumnVal}
				localStorageStr={lsStylesFilterValStr}
			/>
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={"styles_table_attr"}
			/>
			<DataExportModal
				modal={exportModal}
				handleCloseModal={handleCloseExportModal}
				filterColumnVal={buildApiFilter()}
				exportTypeOptions={stylesDashboardExportOptions}
			/>
			<RemarkModal
				modal={remarkModal}
				setModal={setRemarkModal}
				styles={reviewStyles}
				onSubmitSuccess={(remarkResult) => {
					if (remarkResult?.style_remarks) {
						remarkResult.style_remarks.forEach((sr) => {
							if (sr?.style_id) patchStyleEverywhere(sr.style_id, { remark_id: sr.remark_id });
						});
					}
					setReviewStyles([]);
					setRowSelectionModel([]);
				}}
			/>
			<ViewEditRemarkModal
				modal={viewEditRemarkModal}
				setModal={setViewEditRemarkModal}
				styleData={selectedStyleForRemark}
				onSubmitSuccess={reloadStyles}
			/>
		</Stack>
	);
};

export default StylesDashboard;
